import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, Check, Paperclip, Send } from 'lucide-react'
import { toast } from 'sonner'
import { LoadingButton } from '@/components/loading-button'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { InlineFieldInput } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import {
  type ActionInvoice,
  type ActionPaymentDraft,
  type ActionPaymentFields,
  useActionPaymentDraft,
  useRecordActionPayment,
} from '@/features/invoices/data/action-payments'
import { PaymentReceiptInput } from '@/features/invoices/detail/payments'
import { getApiErrorMessage } from '@/lib/api-error'
import { formatMoney } from '@/lib/utils'
import { ComposeTextarea } from '@/components/compose-field'
import { EmailRecipientsField } from '@/components/email-recipients-field'
import { useEmailRecipients } from '@/components/use-email-recipients'

type Method = ActionPaymentFields['payment_method']

const METHODS: ReadonlyArray<{ value: Method; label: string }> = [
  { value: 'ach', label: 'ACH' },
  { value: 'card', label: 'Card' },
  { value: 'check', label: 'Check' },
  { value: 'wire', label: 'Wire' },
  { value: 'portal', label: 'Portal' },
]

function today(): string {
  return new Date().toLocaleDateString('en-CA')
}

function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '')
    reader.onerror = () => reject(reader.error ?? new Error(`${file.name} could not be read`))
    reader.readAsDataURL(file)
  })
}

/**
 * Record a payment accounting made, with its proof, from the action item about the invoice (USA-103).
 * The invoice is marked paid and the item closes in one step; a supplier we pay in advance first gets
 * its confirmation drafted here, with the proof attached, to review and send.
 */
export function RecordPaymentPanel({
  invoice,
  actionSource,
  actionId,
  onCancel,
  onRecorded,
  supplierName,
}: {
  invoice: ActionInvoice
  actionSource: ActionPaymentFields['action_source']
  actionId: number
  onCancel: () => void
  onRecorded?: () => void
  /** Who holds the credit, for "credit on account with Garland"; "this supplier" when not given. */
  supplierName?: string | null
}) {
  const draft = useActionPaymentDraft(invoice.uuid)
  const record = useRecordActionPayment(invoice.uuid)
  const [idempotencyKey] = useState(
    () =>
      `${actionSource}-${actionId}-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`}`,
  )
  const [method, setMethod] = useState<Method>(invoice.payment_method ?? 'ach')
  const [amount, setAmount] = useState(invoice.balance ?? '')
  const [paidOn, setPaidOn] = useState(today)
  const [arrivesOn, setArrivesOn] = useState('')
  const [proof, setProof] = useState<File | null>(null)
  const [proofSource, setProofSource] = useState<'manual_upload' | 'clipboard_paste'>('manual_upload')
  const [email, setEmail] = useState<(ActionPaymentDraft & { body: string }) | null>(null)
  const recipients = useEmailRecipients(
    email?.to_email ? { to: [email.to_email], cc: email.cc_emails ?? [] } : undefined,
  )

  const fields: ActionPaymentFields = {
    action_source: actionSource,
    action_id: actionId,
    payment_method: method,
    paid_on: paidOn,
    arrives_on: method !== 'card' && arrivesOn ? arrivesOn : null,
    amount,
  }
  const amountValid = Number(amount) > 0
  const ready = amountValid && Boolean(paidOn) && (!invoice.pays_in_advance || proof !== null)
  const pending = draft.isPending || record.isPending

  const save = async (confirmationBody: string | null, note?: string | null) => {
    let encoded: string | null = null
    if (proof) {
      try {
        encoded = await readBase64(proof)
      } catch (error) {
        toast.error(getApiErrorMessage(error, 'The proof could not be read'))
        return
      }
    }
    record.mutate(
      {
        ...fields,
        idempotency_key: idempotencyKey,
        ...(confirmationBody !== null ? recipients.override : {}),
        proof: proof && encoded ? [{ filename: proof.name, content_type: proof.type, content_base64: encoded }] : [],
        confirmation_body: confirmationBody,
      },
      {
        onSuccess: (result) => {
          if (result.confirmation === 'failed') {
            toast.error(result.error ?? 'Payment recorded, but the email did not send')
          } else if (result.confirmation === 'sent') {
            toast.success('Payment recorded and the supplier emailed')
          } else {
            toast.success(note || result.error ? `Payment recorded. ${note || result.error}` : 'Payment recorded')
          }
          onRecorded?.()
        },
        onError: (error) => toast.error(getApiErrorMessage(error, 'Payment not recorded')),
      },
    )
  }

  const next = () =>
    draft.mutate(fields, {
      onSuccess: (result) => {
        if (result.pays_in_advance && result.body_text) {
          setEmail({ ...result, body: result.body_text })
        } else if (result.pays_in_advance && !proof) {
          toast.error('Add the payment proof')
        } else {
          void save(null, result.no_email_reason)
        }
      },
      onError: (error) => toast.error(getApiErrorMessage(error, 'Payment not recorded')),
    })

  if (email) {
    return (
      <section aria-label="Payment confirmation" className="space-y-3">
        <EmailRecipientsField state={recipients} toName={email.to_name} disabled={record.isPending} />
        <div className="space-y-1">
          <Label htmlFor={`payment-confirmation-${actionId}`} className="sr-only">
            Confirmation
          </Label>
          <ComposeTextarea
            id={`payment-confirmation-${actionId}`}
            value={email.body}
            onChange={(event) => setEmail({ ...email, body: event.target.value })}
            rows={10}
            className="min-h-[180px]"
          />
          {proof && (
            <p className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
              <Paperclip className="size-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">{proof.name}</span>
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <LoadingButton
            size="sm"
            loading={record.isPending}
            loadingText="Sending..."
            disabled={!email.body.trim()}
            onClick={() => void save(email.body.trim())}
          >
            <Send className="mr-1 h-3 w-3" /> Record &amp; send
          </LoadingButton>
          <Button
            size="sm"
            variant="ghost"
            disabled={record.isPending}
            onClick={() => {
              setEmail(null)
              recipients.reset()
            }}
          >
            <ArrowLeft className="mr-1 h-3 w-3" /> Back
          </Button>
        </div>
      </section>
    )
  }

  return (
    <section aria-label="Record payment" className="space-y-3">
      {invoice.credit_on_account && (
        <p className="text-sm">
          <span className="font-medium">{formatMoney(invoice.credit_on_account)} credit on account</span>
          <span className="text-foreground/80"> with {supplierName || 'this supplier'}. </span>
          <Link
            to="/invoices/$invoiceId"
            params={{ invoiceId: invoice.uuid }}
            className="font-medium text-foreground underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground"
          >
            Apply it to this invoice →
          </Link>
        </p>
      )}
      <div className="space-y-1">
        <p className="text-sm font-medium leading-none">Paid by</p>
        <ReviewChoiceGroup label="Paid by" value={method} options={METHODS} onChange={setMethod} disabled={pending} />
      </div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(10rem,1fr))] gap-3">
        <div className="space-y-1">
          <Label htmlFor={`payment-amount-${actionId}`}>Amount</Label>
          <InlineFieldInput
            id={`payment-amount-${actionId}`}
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value.replace(/[$,\s]/g, ''))}
            aria-invalid={amount !== '' && !amountValid}
            className="tabular-nums"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`payment-date-${actionId}`}>Paid on</Label>
          <Input
            id={`payment-date-${actionId}`}
            type="date"
            value={paidOn}
            onChange={(event) => setPaidOn(event.target.value)}
          />
        </div>
        {method !== 'card' && (
          <div className="space-y-1">
            <Label htmlFor={`payment-arrives-${actionId}`}>Arrives</Label>
            <Input
              id={`payment-arrives-${actionId}`}
              type="date"
              value={arrivesOn}
              min={paidOn}
              onChange={(event) => setArrivesOn(event.target.value)}
            />
          </div>
        )}
      </div>
      <PaymentReceiptInput
        label="Proof"
        file={proof}
        source={proofSource}
        onChange={(file, source) => {
          setProof(file)
          setProofSource(source)
        }}
      />
      <div className="flex flex-wrap gap-2">
        <LoadingButton
          size="sm"
          loading={pending}
          loadingText={invoice.pays_in_advance ? 'Drafting...' : 'Saving...'}
          disabled={!ready}
          onClick={next}
        >
          <Check className="mr-1 h-3 w-3" /> {invoice.pays_in_advance ? 'Review email' : 'Record payment'}
        </LoadingButton>
        <Button size="sm" variant="ghost" disabled={pending} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </section>
  )
}
